import { useCallback } from 'react';
import { useAppSelector } from '@/store/hooks';

function getShopHandle(shopDomain: string): string {
  // Extract shop handle from domain like "sultans-playground.myshopify.com"
  return shopDomain.replace('.myshopify.com', '');
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If accessing window.top throws (cross-origin), we're in an iframe
    return true;
  }
}

export function useShopifyAdmin() {
  const shop = useAppSelector((state) => state.auth.shop);

  const openOrderInAdmin = useCallback((orderId: string | number) => {
    if (!shop?.shop) return;

    const shopHandle = getShopHandle(shop.shop);
    const url = `https://admin.shopify.com/store/${shopHandle}/orders/${orderId}`;

    if (isInIframe()) {
      // In Shopify embedded app - use open with _blank to open in new tab
      // This is the recommended approach per Shopify App Home docs
      window.open(url, '_blank');
    } else {
      // Standalone app - standard window.open
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [shop]);

  const openCustomerInAdmin = useCallback((customerId: string | number) => {
    if (!shop?.shop) return;

    const shopHandle = getShopHandle(shop.shop);
    const url = `https://admin.shopify.com/store/${shopHandle}/customers/${customerId}`;

    if (isInIframe()) {
      window.open(url, '_blank');
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [shop]);

  return {
    openOrderInAdmin,
    openCustomerInAdmin,
    isEmbedded: isInIframe(),
    shopHandle: shop?.shop ? getShopHandle(shop.shop) : null,
  };
}
